import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { workoutService } from '../services/workoutService';
import { useCurrentWorkout } from '../contexts/WorkoutContext';
import { resolveMediaUrl } from '../utils/mediaUrl';
import { LazyImage } from '../components/LazyMedia';
import './WorkoutLibrary.css';

const WorkoutLibrary = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [completedWorkoutIds, setCompletedWorkoutIds] = useState([]);
  const { workoutProgress, workout: currentWorkout } = useCurrentWorkout();
  const workoutsLoadedRef = useRef(false);
  const completedLoadedRef = useRef(false);

  useEffect(() => {
    loadWorkouts();
    loadCompletedWorkouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadWorkouts = async () => {
    if (workoutsLoadedRef.current) {
      return;
    }
    workoutsLoadedRef.current = true;
    try {
      setLoading(true);
      const data = await workoutService.getWorkouts({ limit: 100 });
      setWorkouts(data.items || []);
    } catch (error) {
      console.error('Error loading workouts:', error);
      workoutsLoadedRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  const loadCompletedWorkouts = async () => {
    if (completedLoadedRef.current) {
      return;
    }
    completedLoadedRef.current = true;
    try {
      const ids = await workoutService.getCompletedWorkoutIds();
      setCompletedWorkoutIds(ids);
    } catch (error) {
      console.log('Error loading completed workouts');
      completedLoadedRef.current = false;
    }
  };

  const activeWorkoutProgress = useMemo(
    () => (workoutProgress && !workoutProgress.completed ? workoutProgress : null),
    [workoutProgress]
  );

  const months = [
    { id: 'all', name: 'Все месяцы' },
    { id: '1', name: 'Месяц 1' },
    { id: '2', name: 'Месяц 2' },
    { id: '3', name: 'Месяц 3' },
    { id: '4', name: 'Месяц 4' }
  ];

  // Generate gradient based on workout number
  const getWorkoutGradient = (workoutNumber) => {
    const gradients = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
      'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      'linear-gradient(135deg, #ff9a56 0%, #ff6a88 100%)',
      'linear-gradient(135deg, #ee0979 0%, #ff6a00 100%)',
      'linear-gradient(135deg, #f12711 0%, #f5af19 100%)',
    ];
    return gradients[(workoutNumber - 1) % gradients.length];
  };

  const filteredWorkouts = workouts.filter((workout) => {
    const matchesMonth = selectedMonth === 'all' || workout.month === selectedMonth;
    const matchesSearch = workout.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         workout.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesMonth && matchesSearch;
  });

  return (
    <div className="workout-library">
      <div className="library-header">
        <h2>Библиотека Тренировок</h2>
        <p>Более 500 профессиональных видео-тренировок на все группы мышц</p>
      </div>

      {/* Search and Filters */}
      <div className="filters-section">
        <div className="search-bar">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Поиск тренировок..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="level-filter">
          {months.map(month => (
            <button
              key={month.id}
              className={`level-btn ${selectedMonth === month.id ? 'active' : ''}`}
              onClick={() => setSelectedMonth(month.id)}
            >
              {month.name}
            </button>
          ))}
        </div>
      </div>

      {/* Workouts Grid */}
      {loading ? (
        <div className="loading-state">Загрузка тренировок...</div>
      ) : (
        <div className="workouts-grid">
          {filteredWorkouts.map((workout, index) => {
            const isCompleted = completedWorkoutIds.includes(workout.id);
            const isActive = activeWorkoutProgress && activeWorkoutProgress.workoutId === workout.id;
            const isCurrent = currentWorkout && currentWorkout.id === workout.id;

            // First workout is always unlocked if no workouts completed yet
            const isFirstWorkout = workout.workoutNumber === 1;
            const hasNoProgress = completedWorkoutIds.length === 0 && !activeWorkoutProgress;

            // Block if not completed, not active, not current, and not the first workout when no progress
            const isBlocked = !isCompleted && !isActive && !isCurrent && !(isFirstWorkout && hasNoProgress);

            return (
              <Link
                key={workout.id}
                to={`/app/workouts/${workout.id}`}
                className={`workout-card ${isBlocked ? 'blocked' : ''} ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                onClick={(e) => {
                  if (isBlocked) {
                    e.preventDefault();
                  }
                }}
              >
                <div className="workout-image-container">
                  {workout.previewUrl ? (
                    <LazyImage
                      src={resolveMediaUrl(workout.previewUrl)}
                      alt={workout.name}
                      className="workout-image-placeholder"
                      rootMargin={400}
                      style={{
                        width: '100%',
                        height: '100%'
                      }}
                    />
                  ) : (
                    <div
                      className="workout-image-placeholder"
                      style={{
                        background: getWorkoutGradient(workout.workoutNumber)
                      }}
                    />
                  )}
                  <div className="workout-overlay">
                    {isCompleted ? (
                      <button className="play-btn completed">✓ Пройдено</button>
                    ) : isActive ? (
                      <button className="play-btn active">▶ Продолжить</button>
                    ) : isBlocked ? (
                      <button className="play-btn blocked">🔒 Заблокировано</button>
                    ) : (
                      <button className="play-btn">▶ Начать</button>
                    )}
                  </div>
                  <div className="workout-level-badge">
                    Месяц {workout.monthNumber}
                  </div>
                  {isActive && (
                    <div className="active-badge">Активная</div>
                  )}
                  {isCompleted && (
                    <div className="completed-badge">✓ Пройдено</div>
                  )}
                </div>

                <div className="workout-info">
                  <h3>{workout.name}</h3>
                  <p className="workout-description">{workout.description}</p>

                  <div className="workout-stats">
                    <div className="stat">
                      <span className="stat-icon">📋</span>
                      <span>{workout.exerciseCount} упр.</span>
                    </div>
                    <div className="stat">
                      <span className="stat-icon">📅</span>
                      <span>Месяц {workout.monthNumber}</span>
                    </div>
                  </div>

                  <button
                    className={`btn-primary btn-full ${isBlocked && !isCompleted ? 'blocked' : ''} ${isCompleted ? 'completed' : ''}`}
                    disabled={isBlocked && !isCompleted}
                  >
                    {isCompleted ? '✓ Пройдено' : isActive ? 'Продолжить тренировку' : isBlocked ? 'Заблокировано' : 'Начать тренировку'}
                  </button>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {!loading && filteredWorkouts.length === 0 && (
        <div className="no-results">
          <div className="no-results-icon">😔</div>
          <h3>Тренировки не найдены</h3>
          <p>Попробуйте изменить фильтры или поисковый запрос</p>
        </div>
      )}
    </div>
  );
};

export default WorkoutLibrary;
